import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  getAuth, // Added for HTTP function calls
  createUserWithEmailAndPassword // Added for direct admin creation
} from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db, app } from '../firebase/config';
import { Company, User } from '../types/index';
import { buildFunctionUrl } from '../utils/functionsUrl';
import { buildAuthHeaders } from '../utils/authHeaders';

export class AuthService {
  // 認証永続化を設定
  static async setPersistence() {
    try {
      // SafariでのIndexedDBエラーを避けるため、セッション永続化を推奨
      await setPersistence(auth, browserSessionPersistence);
    } catch (error) {
      console.warn('認証永続化設定エラー:', error);
      // フォールバックとしてローカル永続化を試行
      try {
        await setPersistence(auth, browserLocalPersistence);
      } catch (fallbackError) {
        console.warn('認証永続化の設定に失敗しました:', fallbackError);
      }
    }
  }

  // メール/パスワード認証（管理者用） - Firestore で直接認証
  /**
   * 管理者ログイン
   *
   * 従来はブラウザが Firestore から adminPassword を読み取って比較していたが、
   * その方式では Firebase Auth を経由しないため request.auth が null となり、
   * Firestore ルールも Cloud Functions もアクセス元を識別できなかった。
   *
   * 現在は adminLogin(Cloud Functions) がサーバー側で照合し、カスタムトークンを
   * 発行する。それを signInWithCustomToken でサインインすることで Firebase Auth の
   * セッションが確立し、request.auth が利用可能になる。
   *
   * 設計書: レフリアメモ/認証の一本化について.md
   */
  static async signInWithEmailPassword(companyId: string, email: string, password: string) {
    try {
      // 1. 認証永続化を設定
      await this.setPersistence();

      // 2. サーバー側で照合し、カスタムトークンを取得する
      //    パスワードは送信するだけで、ブラウザ側では読み取らない
      const response = await fetch(buildFunctionUrl('adminLogin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: companyId.trim(),
          email: email.trim(),
          password,
        }),
      });

      if (!response.ok) {
        let serverMessage: string | undefined;
        try {
          serverMessage = (await response.json())?.error;
        } catch {
          // JSON でない場合は既定のメッセージを使う
        }
        throw new Error(
          serverMessage || '会社ID、メールアドレス、またはパスワードが正しくありません。'
        );
      }

      const { customToken, uid, companyDocId } = await response.json();

      // 3. カスタムトークンで Firebase Auth にサインインする
      //    ここで request.auth が有効になる
      await signInWithCustomToken(auth, customToken);

      // 4. 会社情報とユーザー情報を取得する
      const companySnap = await getDoc(doc(db, 'companies', companyDocId));
      if (!companySnap.exists()) {
        throw new Error('会社情報が見つかりません。');
      }
      const companyData = companySnap.data() as any;

      const userSnap = await getDoc(doc(db, 'users', uid));
      const userData = (userSnap.exists() ? userSnap.data() : {}) as any;

      // 5. 端末IDを保存する（失敗してもログインは継続）
      const deviceId = await this.generateDeviceId();
      try {
        await setDoc(
          doc(db, 'users', uid),
          { deviceId, companyId: companyDocId },
          { merge: true }
        );
      } catch (userError) {
        console.warn('[管理者ログイン] ユーザー情報の更新に失敗:', userError);
      }

      // 6. デバイス登録を実行（Cloud Functions経由。失敗してもログインは継続）
      try {
        const { coarse, fine } = await this.generateFingerprints();
        const deviceResponse = await fetch(buildFunctionUrl('registerDevice'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId: companyDocId,
            corporateCode: companyData.corporateCode,
            coarseFingerprint: coarse,
            fineFingerprint: fine,
          }),
        });

        if (!deviceResponse.ok) {
          console.error('[管理者ログイン] デバイス登録失敗:', deviceResponse.status);
        }
      } catch (error: any) {
        console.error('[管理者ログイン] デバイス登録処理エラー:', error?.message || String(error));
      }

      // 7. 管理者セッションIDを保存する
      //    request.auth への移行後も、既存の参照箇所(32箇所)のために維持する
      if (userData.userType === 'admin' || userData.userType === 'superadmin') {
        const storage = typeof window !== 'undefined' ? window.localStorage : null;
        if (storage) {
          storage.setItem('adminUserId', uid);
        }
      }

      return {
        user: {
          uid,
          firebaseUid: uid,
          email: userData.email ?? email.trim(),
          userType: userData.userType,
          displayName: userData.displayName,
          companyId: companyDocId,
        } as any,
        company: { id: companyDocId, ...companyData } as any,
      };
    } catch (error: any) {
      console.error('管理者ログインエラー:', error);
      if (error.message) {
        throw new Error(error.message);
      }
      throw new Error('管理者ログインに失敗しました。しばらく時間をおいて再度お試しください。');
    }
  }

  // サインアウト
  static async signOut() {
    try {
      // localStorage から管理者セッション ID をクリア
      localStorage.removeItem('adminUserId');
      // 自分でログアウトした場合はログイン画面に戻す
      localStorage.removeItem('loginMethod');
      
      // Firebase Auth からサインアウト
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('サインアウトエラー:', error);
      throw error;
    }
  }

  // 管理者アカウント作成（Flutter版のcreateAdminAccountと同等）
  /**
   * 管理者アカウントを作成する。
   *
   * 以前はブラウザ側で Firebase Auth のユーザー作成と users ドキュメントの
   * 書き込みを行っていたが、②のルール是正で他人のドキュメントを作成できなく
   * なったこと、平文パスワードを保存していたことから、処理をサーバー側
   * (createCompanyAdmin) へ移した。
   *
   * 設計書: レフリアメモ/認証の一本化について.md
   */
  static async createAdminAccount({
    email,
    password,
    displayName,
    userType,
    department,
    role,
    companyId,
    companyName,
  }: {
    email: string;
    password: string;
    displayName: string;
    userType: 'admin' | 'superadmin';
    department?: string;
    role?: string;
    companyId?: string;
    companyName?: string;
    /** 呼び出し元の識別はIDトークンで行うため使用しない（互換のため受け取る） */
    currentUserId?: string;
  }) {
    const response = await fetch(buildFunctionUrl('createCompanyAdmin'), {
      method: 'POST',
      headers: await buildAuthHeaders(),
      body: JSON.stringify({
        email,
        password,
        displayName,
        userType,
        department,
        role,
        companyId,
        companyName,
      }),
    });

    if (!response.ok) {
      let message: string | undefined;
      try {
        message = (await response.json())?.error;
      } catch {
        // JSON でない場合は既定のメッセージを使う
      }
      throw new Error(message || '管理者アカウントの作成に失敗しました。');
    }

    const { uid } = await response.json();
    return { success: true, uid };
  }

  // ユーザー削除（Flutter版のdeleteUserFromAuthと同等）
  // ✅ 修正: docId（Firestore ドキュメント ID）を受け取る
  static async deleteUserFromAuth(docId: string, currentUserId: string) {
    try {
      // 管理者もカスタムトークンでサインインしているため、IDトークンで認証する
      const response = await fetch(
        buildFunctionUrl('deleteUserFromAuth'),
        {
          method: 'POST',
          headers: await buildAuthHeaders(),
          body: JSON.stringify({ docId }), // ✅ 修正: uid の代わりに docId を送信
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'ユーザー削除に失敗しました');
      }
      
      const result = await response.json();
      return result;
    } catch (error: any) {
      console.error('ユーザー削除エラー:', error);
      
      // CORSエラーの場合の特別処理
      if (error.message?.includes('CORS') || error.message?.includes('blocked by CORS')) {
        throw new Error('ネットワークエラーが発生しました。Firebase Functionsの設定を確認してください。');
      }
      
      // エラーメッセージを日本語で表示
      if (error.code === 'functions/permission-denied') {
        throw new Error('管理者権限が必要です。');
      } else if (error.code === 'functions/unauthenticated') {
        throw new Error('認証が必要です。再度ログインしてください。');
      } else if (error.code === 'functions/invalid-argument') {
        throw new Error('自分自身を削除することはできません。');
      } else if (error.code === 'functions/not-found') {
        throw new Error('削除対象のユーザーが見つかりません。');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('ユーザーの削除に失敗しました。しばらく時間をおいて再度お試しください。');
      }
    }
  }

  // パスワードリセットメール送信
  // ✅ 修正: Cloud Function 経由でパスワードリセットメールを送信（SendGrid 使用）
  static async sendPasswordResetEmail(companyId: string, email: string) {
    try {
      // Cloud Function を呼び出す
      const response = await fetch(
        buildFunctionUrl('sendPasswordResetEmailWithCompany'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ companyId, email }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'パスワードリセットメールの送信に失敗しました');
      }

    } catch (error: any) {
      console.error('[sendPasswordResetEmail] エラー:', {
        message: error.message,
        fullError: error
      });
      throw error;
    }
  }

  // 管理者から一般ユーザーに切り替え
  static async switchToCorporateUser() {
    try {
      
      // 現在のユーザーを取得
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('ユーザーが認証されていません');
      }

      // ユーザーの会社情報を取得
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        throw new Error('ユーザー情報が見つかりません');
      }

      const userData = userDoc.data();
      const companyDocId = userData.companyId; // Firestore の実際のドキュメント ID

      if (!companyDocId) {
        throw new Error('会社情報が見つかりません');
      }

      // 会社情報を取得（Firestore の実際のドキュメント ID を使用）
      const companyDoc = await getDoc(doc(db, 'companies', companyDocId));
      if (!companyDoc.exists()) {
        throw new Error('会社情報が見つかりません');
      }

      const companyData = companyDoc.data();
      const corporateCode = companyData?.corporateCode;
      
      if (!corporateCode) {
        throw new Error('会社 ID が見つかりません');
      }

      // 一般ユーザーアカウントでログイン
      // 会社コード、メール、パスワードを使用して新しい signInWithEmailPassword を呼び出す
      // TODO: この機能の実装または削除を検討する必要があります
      // 現在のところ、switchToCorporateUser は不完全な実装のため無効化されています
      
    } catch (error) {
      console.error('ユーザー切り替えエラー:', error);
      throw error;
    }
  }

  // 端末IDを生成（フィンガープリント方式）
  private static async generateDeviceId(): Promise<string> {
    try {
      // LocalStorageから既存のフィンガープリントを取得（新しいキー名を使用）
      const existingId = localStorage.getItem('liavoice_device_fingerprint');
      if (existingId) {
        return existingId;
      }

      // フィンガープリントを生成
      const fingerprint = await this.generateFingerprint();
      const deviceId = fingerprint;

      // LocalStorageに保存（新しいキー名を使用）
      localStorage.setItem('liavoice_device_fingerprint', deviceId);

      return deviceId;
    } catch (error) {
      console.error('[Web版] フィンガープリント生成エラー:', error);
      // フォールバック: タイムスタンプのみで生成
      const fallbackId = `${Date.now()}_fallback`;
      return fallbackId;
    }
  }

  // フィンガープリントを生成
  private static async generateFingerprint(): Promise<string> {
    // ブラウザ情報を収集
    const platform = navigator.platform || 'unknown';
    const userAgent = navigator.userAgent || 'unknown';
    const vendor = navigator.vendor || 'unknown';
    const language = navigator.language || 'unknown';
    
    // ハードウェア情報（利用可能な場合）
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    let hardwareConcurrency = 'unknown';
    let maxTouchPoints = 'unknown';
    
    if (navigator.hardwareConcurrency) {
      hardwareConcurrency = navigator.hardwareConcurrency.toString();
    }
    if (navigator.maxTouchPoints) {
      maxTouchPoints = navigator.maxTouchPoints.toString();
    }
    
    // タイムゾーン情報
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
    
    // 画面情報
    const screenInfo = {
      width: screen.width || 0,
      height: screen.height || 0,
      colorDepth: screen.colorDepth || 0,
      pixelRatio: window.devicePixelRatio || 1
    };

    // 収集した情報をまとめる
    const payload = JSON.stringify({
      platform,
      userAgent,
      vendor,
      language,
      hardwareConcurrency,
      maxTouchPoints,
      timezone,
      screen: screenInfo
    });

    // SHA-256でハッシュ化
    const encoder = new TextEncoder();
    const data = encoder.encode(payload);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const fingerprint = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return fingerprint;
  }

  // 粗い/詳細の2段フィンガープリントを生成（同一物理端末の再利用に利用）
  static async generateFingerprints(): Promise<{ coarse: string; fine: string }> {
    const platform = navigator.platform || 'unknown';
    // 画面は大きく丸めて“物理端末”寄りにする
    const screenRounded = {
      w: Math.round((screen.width || 0) / 50) * 50,
      h: Math.round((screen.height || 0) / 50) * 50
    };

    // coarse: OS + 大まかな画面サイズ（ブラウザ差要素は含めない）
    const coarsePayload = JSON.stringify({ platform, screenRounded });

    // fine: 既存の詳細指紋（UA/vendor等を含む）
    const userAgent = navigator.userAgent || 'unknown';
    const vendor = navigator.vendor || 'unknown';
    const finePayload = JSON.stringify({
      platform,
      userAgent,
      vendor,
      language: navigator.language || 'unknown',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      cores: String(navigator.hardwareConcurrency || 'unknown'),
      touch: String(navigator.maxTouchPoints || 'unknown'),
      screen: { width: screen.width || 0, height: screen.height || 0, colorDepth: screen.colorDepth || 0, pixelRatio: window.devicePixelRatio || 1 }
    });

    const hash = async (s: string) => {
      const data = new TextEncoder().encode(s);
      const buf = await crypto.subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
    };

    return {
      coarse: await hash(coarsePayload),
      fine: await hash(finePayload)
    };
  }

  // デバイス登録（外部から呼び出し可能）
  static async registerDeviceManually(companyId: string): Promise<void> {
    return this.registerDevice(companyId);
  }

  // デバイス登録（devicesコレクションに追加し、registeredDevicesを+1）
  private static async registerDevice(companyDocId: string, corporateCode?: string): Promise<void> {
    try {
      // 粗い/詳細フィンガープリントを生成
      const { coarse, fine } = await this.generateFingerprints();
      
      // 会社ドキュメントを取得して現在の登録端末数を確認
      const companyRef = doc(db, 'companies', companyDocId);
      const companySnap = await getDoc(companyRef);
      if (!companySnap.exists()) {
        console.warn('[registerDevice] 会社ドキュメントが見つかりません:', companyDocId);
        return;
      }
      
      const companyData = companySnap.data();

      // corporateCode が指定されなかった場合は Firestore から取得
      const code = corporateCode || companyData?.corporateCode || companyDocId;
      
      // 物理端末ID（coarse）とデバイスID（fine）
      const physicalDeviceId = `${code}_${coarse}`;
      const deviceId = `${code}_${fine}`;

      // 既存の物理端末（coarse一致）を検索し、あれば再利用（件数は増やさない）
      const existingPhysicalSnap = await getDocs(
        query(
          collection(db, 'devices'),
          where('companyId', '==', companyDocId),
          where('physicalDeviceId', '==', physicalDeviceId),
          where('isActive', '==', true)
        )
      );
      if (!existingPhysicalSnap.empty) {
        const ref = existingPhysicalSnap.docs[0].ref;
        await updateDoc(ref, {
          lastActiveAt: serverTimestamp(),
          isActive: true,
          // 最新の詳細指紋を保持（監査・識別の補助）
          fineFingerprint: fine
        });
        return;
      }

      // フォールバック: 旧形式で作られた既存端末（physicalDeviceId未設定）を再利用
      // 会社内で最近のアクティブ端末を少数取得し、physicalDeviceIdが無いものに付与して再利用する
      const fallbackCandidatesSnap = await getDocs(
        query(
          collection(db, 'devices'),
          where('companyId', '==', companyDocId),
          where('isActive', '==', true),
          limit(10)
        )
      );
      const fallbackDoc = fallbackCandidatesSnap.docs.find(d => !d.data().physicalDeviceId);
      if (fallbackDoc) {
        await updateDoc(fallbackDoc.ref, {
          physicalDeviceId: physicalDeviceId,
          coarseFingerprint: coarse,
          fineFingerprint: fine,
          lastActiveAt: serverTimestamp(),
          isActive: true,
        });
        return;
      }

      // 新規端末: 実際のアクティブ端末数を取得
      const activeDevicesSnapshot = await getDocs(
        query(
          collection(db, 'devices'),
          where('companyId', '==', companyDocId),
          where('isActive', '==', true)
        )
      );
      const actualDeviceCount = activeDevicesSnapshot.docs.length;

      // 会社ドキュメントのデータ（既に取得済み）
      const currentPlan = companyData?.currentPlan;
      
      // プラン上限を取得（companiesのmaxDevicesフィールドまたはプラン名から）
      const getMaxDevicesForPlan = (planName: string) => {
        const planLimits: { [key: string]: number } = {
          'プラン50': 50,
          'プラン100': 100,
          'プラン200': 200,
          'プラン300': 300,
          'プラン400': 400,
          'プラン500': 500,
        };
        return planLimits[planName] || 50;
      };
      
      // maxDevicesフィールドがあればそれを使う、なければプラン名から取得
      const maxDevices = companyData?.maxDevices || getMaxDevicesForPlan(currentPlan || 'プラン50');
      const currentCount = companyData?.registeredDevices || 0;

      // 実数とcompaniesの現在値の大きい方を基準にする
      const effectiveBaseCount = currentCount > actualDeviceCount ? currentCount : actualDeviceCount;
      const nextCount = effectiveBaseCount + 1;
      
      // maxDevicesを超えても登録可能（上限チェックを削除）
      // 注意: maxDevicesを超えた場合でも、デバイスは登録され、registeredDevicesは増加します

      // デバイスをdevicesコレクションに追加（新規のみ）
      const deviceRef = doc(db, 'devices', deviceId);

      const deviceData = {
        deviceId: deviceId,
        companyId: companyDocId,
        physicalDeviceId: physicalDeviceId,
        coarseFingerprint: coarse,
        fineFingerprint: fine,
        isActive: true,
        lastActiveAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      };

      await setDoc(deviceRef, deviceData);

      // 会社ドキュメントの登録端末数を更新
      await updateDoc(companyRef, {
        registeredDevices: nextCount,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      // デバイス登録に失敗してもログインは継続
      console.error('デバイス登録エラー:', error);
    }
  }

  // 新規管理者ウェルカムメール送信（SendGrid使用）
  static async sendAdminWelcomeEmail(email: string, displayName: string, companyName: string, companyId: string) {
    try {
      const response = await fetch(
        buildFunctionUrl('sendAdminWelcomeEmail'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, displayName, companyName, companyId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'ウェルカムメール送信に失敗しました');
      }

    } catch (error: any) {
      console.error('[sendAdminWelcomeEmail] エラー:', {
        message: error.message,
        fullError: error
      });
      throw error;
    }
  }
}

